// src/components/TravelPlanner.jsx
import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import "bootstrap-icons/font/bootstrap-icons.css";
import HeaderDesktop from "../_components/_global/HeaderDesktop";
import MapComponent from "../_components/main/MapComponent";
import { LoadScript } from "@react-google-maps/api";

const mapLocations = [
  { id: 1, name: "Forbidden City", lat: 39.9163, lng: 116.3972 },
  { id: 2, name: "Jingshan Park", lat: 39.9255, lng: 116.3969 },
  { id: 3, name: "Temple of Heaven", lat: 39.8822, lng: 116.4066 },
  { id: 4, name: "798 Art District", lat: 39.9844, lng: 116.4955 },
  // 添加更多位置...
];

// Generate scoped ID to prevent style conflicts
function useScopeId(prefix = "tp") {
  return useMemo(
    () => `${prefix}-${Math.random().toString(36).slice(2, 8)}`,
    [prefix]
  );
}

/**
 * TravelPlanner Component
 * props:
 *  - cityName: string (default: "Montreal")
 *  - onMapClick: (location) => void
 *  - onItinerarySelect: (item) => void
 */
export default function TravelPlanner({
  cityName = "Montreal",
  onMapClick,
  onItinerarySelect,
}) {
  const scope = useScopeId();
  const nav = useNavigate();
  const [currentCity, setCurrentCity] = React.useState("Montreal");

  // State management
  const [leftCollapsed, setLeftCollapsed] = useState(false);
  const [rightCollapsed, setRightCollapsed] = useState(false);
  const [activeLeftTab, setActiveLeftTab] = useState("preferences");
  const [activeRightTab, setActiveRightTab] = useState("itinerary");
  const [selectedItem, setSelectedItem] = useState(0);
  const [chatInput, setChatInput] = useState("");

  // Sample data
  const itineraryItems = [
    {
      id: 1,
      title: "Forbidden City",
      rating: 4.8,
      desc: "Explore the magnificent imperial palace complex",
      day: 1,
      time: "09:00",
      price: 60,
      duration: "3 hours",
    },
    {
      id: 2,
      title: "Jingshan Park",
      rating: 4.5,
      desc: "Best panoramic views of the Forbidden City",
      day: 1,
      time: "14:00",
      price: 10,
      duration: "1 hour",
    },
    {
      id: 3,
      title: "Quanjude Roast Duck",
      rating: 4.4,
      desc: "Taste authentic Montreal roast duck",
      day: 1,
      time: "19:00",
      price: 280,
      duration: "2 hours",
    },
    {
      id: 4,
      title: "Temple of Heaven",
      rating: 4.7,
      desc: "Imperial complex for annual prayers to Heaven",
      day: 2,
      time: "08:00",
      price: 35,
      duration: "2 hours",
    },
    {
      id: 5,
      title: "Old Montreal Noodles",
      rating: 4.3,
      desc: "Experience authentic Montreal flavors",
      day: 2,
      time: "12:00",
      price: 35,
      duration: "1 hour",
    },
    {
      id: 6,
      title: "798 Art District",
      rating: 4.5,
      desc: "Hub of modern art and creative culture",
      day: 2,
      time: "14:00",
      price: 0,
      duration: "3 hours",
    },
  ];

  const recommendations = [
    {
      id: 1,
      title: "Lama Temple",
      desc: "Based on your cultural interests",
      rating: 4.6,
      time: "1-2 hours",
    },
    {
      id: 2,
      title: "Ghost Street Food Tour",
      desc: "Evening culinary adventure",
      rating: 4.4,
      time: "1-2 hours",
    },
    {
      id: 3,
      title: "Houhai Bar Street",
      desc: "Evening stroll destination",
      rating: 4.2,
      time: "2-3 hours",
    },
  ];

  const handleItemSelect = (index, item) => {
    setSelectedItem(index);
    if (onItinerarySelect) onItinerarySelect(item);
  };

  const handleSendChat = () => {
    if (chatInput.trim()) {
      console.log("Sending:", chatInput);
      setChatInput("");
    }
  };

  // CSS styles
  const css = `
    [data-tp="${scope}"] {
      display: flex;
      height: 100vh;
      background: #f5f5f7;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      position: relative;
    }

    /* Sidebar Base */
    [data-tp="${scope}"] .sidebar {
      background: #fff;
      transition: all 0.3s ease;
      overflow-y: auto;
      position: relative;
      box-shadow: 0 0 10px rgba(0,0,0,0.05);
    }
    [data-tp="${scope}"] .sidebar.collapsed { width: 60px !important; }
    [data-tp="${scope}"] .sidebar.collapsed .sidebar-content { display: none; }

    /* Left Sidebar */
    [data-tp="${scope}"] .left-sidebar {
      width: 320px;
      border-right: 1px solid #e5e7eb;
    }

    /* Right Sidebar */
    [data-tp="${scope}"] .right-sidebar {
      width: 380px;
      border-left: 1px solid #e5e7eb;
    }

    /* Toggle Button */
    [data-tp="${scope}"] .toggle-btn {
      position: absolute;
      top: 20px;
      right: 20px;
      background: none;
      border: none;
      cursor: pointer;
      padding: 5px;
      z-index: 10;
      display: flex;
      flex-direction: column;
      gap: 3px;
    }
    [data-tp="${scope}"] .toggle-btn span {
      display: block;
      width: 20px;
      height: 2px;
      background: #6b7280;
      transition: all 0.3s ease;
    }
    [data-tp="${scope}"] .toggle-btn:hover span { background: #374151; }

    /* Headers */
    [data-tp="${scope}"] .sidebar-header {
      padding: 20px;
      border-bottom: 1px solid #e5e7eb;
    }
    [data-tp="${scope}"] .sidebar-header h2 {
      font-size: 18px;
      font-weight: 600;
      color: #1f2937;
    }

    /* Tabs */
    [data-tp="${scope}"] .tab-nav {
      display: flex;
      gap: 10px;
      padding: 15px 20px;
      background: #f9fafb;
      border-bottom: 1px solid #e5e7eb;
    }
    [data-tp="${scope}"] .tab-btn {
      padding: 8px 16px;
      background: #fff;
      border: 1px solid #e5e7eb;
      border-radius: 20px;
      cursor: pointer;
      font-size: 14px;
      color: #6b7280;
      transition: all 0.2s ease;
    }
    [data-tp="${scope}"] .tab-btn:hover {
      background: #f3f4f6;
      border-color: #9ca3af;
    }
    [data-tp="${scope}"] .tab-btn.active {
      background: #1f2937;
      color: #fff;
      border-color: #1f2937;
    }

    /* Filter Section */
    [data-tp="${scope}"] .filter-section {
      padding: 20px;
      border-bottom: 1px solid #e5e7eb;
    }
    [data-tp="${scope}"] .filter-section h3 {
      font-size: 14px;
      font-weight: 600;
      color: #1f2937;
      margin-bottom: 15px;
    }

    /* Dropdown */
    [data-tp="${scope}"] .dropdown {
      width: 100%;
      padding: 10px 15px;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      background: #fff;
      font-size: 14px;
      color: #1f2937;
      cursor: pointer;
    }

    /* Budget */
    [data-tp="${scope}"] .budget-input {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-top: 10px;
    }
    [data-tp="${scope}"] .budget-input input {
      flex: 1;
      padding: 8px 12px;
      border: 1px solid #e5e7eb;
      border-radius: 6px;
      font-size: 14px;
    }
    [data-tp="${scope}"] .budget-display {
      background: #2c3e50;
      color: #fff;
      padding: 12px;
      border-radius: 8px;
      text-align: center;
      font-size: 14px;
      margin-top: 10px;
    }

    /* Quick Filters */
    [data-tp="${scope}"] .filter-item {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 10px;
      cursor: pointer;
      transition: background 0.2s ease;
      border-radius: 8px;
    }
    [data-tp="${scope}"] .filter-item:hover { background: #f9fafb; }

    /* Info Box */
    [data-tp="${scope}"] .info-box {
      background: #f9fafb;
      padding: 15px;
      border-radius: 8px;
      margin-top: 10px;
    }
    [data-tp="${scope}"] .info-row {
      display: flex;
      justify-content: space-between;
      margin-bottom: 8px;
      font-size: 14px;
      color: #4b5563;
    }
    [data-tp="${scope}"] .info-row:last-child { margin-bottom: 0; }

    /* Main Content */
    [data-tp="${scope}"] .main-content {
      flex: 1;
      display: flex;
      position: relative;
      background: #fff;
    }

    /* Map */
    [data-tp="${scope}"] .map-container {
      flex: 1;
      position: relative;
      background: #f0f4f8;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    [data-tp="${scope}"] .map-placeholder {
      text-align: center;
      padding: 40px;
      background: #fff;
      border-radius: 12px;
      box-shadow: 0 4px 6px rgba(0,0,0,0.1);
    }
    [data-tp="${scope}"] .map-controls {
      position: absolute;
      top: 20px;
      right: 20px;
      background: #fff;
      border-radius: 8px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.1);
      overflow: hidden;
    }
    [data-tp="${scope}"] .map-btn {
      width: 40px;
      height: 40px;
      border: none;
      background: #fff;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background 0.2s ease;
      border-bottom: 1px solid #e5e7eb;
    }
    [data-tp="${scope}"] .map-btn:last-child { border-bottom: none; }
    [data-tp="${scope}"] .map-btn:hover { background: #f3f4f6; }

    /* Route Preview */
    [data-tp="${scope}"] .route-preview {
      position: absolute;
      bottom: 20px;
      left: 50%;
      transform: translateX(-50%);
      background: #fff;
      padding: 15px 20px;
      border-radius: 12px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      display: flex;
      align-items: center;
      gap: 20px;
    }

    /* AI Chat */
    [data-tp="${scope}"] .ai-chat {
      width: 380px;
      background: #fff;
      border-left: 1px solid #e5e7eb;
      display: flex;
      flex-direction: column;
    }
    [data-tp="${scope}"] .chat-header {
      padding: 20px;
      border-bottom: 1px solid #e5e7eb;
      background: #f9fafb;
    }
    [data-tp="${scope}"] .chat-messages {
      flex: 1;
      padding: 20px;
      overflow-y: auto;
    }
    [data-tp="${scope}"] .message-bubble {
      background: #f3f4f6;
      padding: 15px;
      border-radius: 12px;
      font-size: 14px;
      line-height: 1.6;
      margin-bottom: 15px;
    }
    [data-tp="${scope}"] .quick-actions {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 10px;
      margin-top: 15px;
    }
    [data-tp="${scope}"] .quick-btn {
      padding: 12px;
      background: #fff;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      font-size: 14px;
      cursor: pointer;
      transition: all 0.2s ease;
    }
    [data-tp="${scope}"] .quick-btn:hover {
      background: #f9fafb;
      border-color: #9ca3af;
    }
    [data-tp="${scope}"] .chat-input-area {
      padding: 20px;
      border-top: 1px solid #e5e7eb;
    }
    [data-tp="${scope}"] .chat-input-wrap {
      display: flex;
      gap: 10px;
    }
    [data-tp="${scope}"] .chat-input {
      flex: 1;
      padding: 12px 16px;
      border: 1px solid #e5e7eb;
      border-radius: 24px;
      font-size: 14px;
      outline: none;
    }
    [data-tp="${scope}"] .chat-input:focus {
      border-color: #3b82f6;
      box-shadow: 0 0 0 3px rgba(59,130,246,0.1);
    }
    [data-tp="${scope}"] .send-btn {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      background: #3b82f6;
      border: none;
      color: #fff;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background 0.2s ease;
    }
    [data-tp="${scope}"] .send-btn:hover { background: #2563eb; }

    /* Progress Card */
    [data-tp="${scope}"] .progress-card {
      background: #f9fafb;
      padding: 20px;
      border-radius: 12px;
      margin-bottom: 20px;
    }
    [data-tp="${scope}"] .progress-bar {
      background: #e5e7eb;
      height: 8px;
      border-radius: 4px;
      overflow: hidden;
      margin: 15px 0;
    }
    [data-tp="${scope}"] .progress-fill {
      background: #10b981;
      height: 100%;
      width: 65%;
      transition: width 0.3s ease;
    }
    [data-tp="${scope}"] .progress-stats {
      display: flex;
      justify-content: space-around;
      margin-top: 20px;
    }

    /* Itinerary Items */
    [data-tp="${scope}"] .itinerary-item {
      background: #fff;
      border: 1px solid #e5e7eb;
      border-radius: 12px;
      padding: 15px;
      margin-bottom: 10px;
      cursor: pointer;
      transition: all 0.2s ease;
    }
    [data-tp="${scope}"] .itinerary-item:hover {
      box-shadow: 0 4px 12px rgba(0,0,0,0.08);
    }
    [data-tp="${scope}"] .itinerary-item.selected {
      border-color: #3b82f6;
      background: #eff6ff;
    }
    [data-tp="${scope}"] .item-header {
      display: flex;
      justify-content: space-between;
      margin-bottom: 10px;
    }
    [data-tp="${scope}"] .item-meta {
      display: flex;
      gap: 15px;
      font-size: 12px;
      color: #6b7280;
      margin-top: 10px;
    }

    /* Recommendations */
    [data-tp="${scope}"] .rec-item {
      background: #fff;
      padding: 15px;
      border-radius: 8px;
      margin-bottom: 10px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      cursor: pointer;
      transition: all 0.2s ease;
    }
    [data-tp="${scope}"] .rec-item:hover {
      transform: translateX(5px);
      box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    }

    /* Responsive */
    @media (max-width: 1200px) {
      [data-tp="${scope}"] .left-sidebar { width: 280px; }
      [data-tp="${scope}"] .ai-chat { width: 320px; }
    }
    @media (max-width: 768px) {
      [data-tp="${scope}"] .left-sidebar,
      [data-tp="${scope}"] .right-sidebar {
        position: absolute;
        top: 0;
        height: 100%;
        z-index: 30;
      }
      [data-tp="${scope}"] .ai-chat { display: none; }
    }
  `;

  const ToggleButton = ({ onClick }) => (
    <button className="toggle-btn" onClick={onClick}>
      <span></span>
      <span></span>
      <span></span>
    </button>
  );

  const TabButton = ({ label, isActive, onClick }) => (
    <button className={`tab-btn ${isActive ? "active" : ""}`} onClick={onClick}>
      {label}
    </button>
  );

  const ItineraryItem = ({ item, index, isSelected, onClick }) => (
    <div
      className={`itinerary-item ${isSelected ? "selected" : ""}`}
      onClick={() => onClick(index, item)}
    >
      <div className="item-header">
        <div style={{ fontSize: 14, fontWeight: 600, color: "#1f2937" }}>
          {item.title}
        </div>
        <div style={{ color: "#f59e0b", fontSize: 14 }}>
          <i className="bi bi-star-fill" style={{ fontSize: 12 }} />{" "}
          {item.rating}
        </div>
      </div>
      <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.5 }}>
        {item.desc}
      </div>
      <div className="item-meta">
        <span>
          Day {item.day} • {item.time}
        </span>
        <span>${item.price}</span>
        <span>{item.duration}</span>
      </div>
    </div>
  );

  return (
    <>
      <style>{css}</style>

      <HeaderDesktop
        active="planner" // 高亮 Trip Planner 导航项
        cityName={currentCity}
        unread={3}
        onSearch={(query) => console.log("Searching:", query)}
      />

      <div data-tp={scope}>
        {/* Left Sidebar - Filters & Options */}
        <aside
          className={`sidebar left-sidebar ${leftCollapsed ? "collapsed" : ""}`}
        >
          <ToggleButton onClick={() => setLeftCollapsed(!leftCollapsed)} />

          <div className="sidebar-content">
            <div className="sidebar-header">
              <h2>Filters & Options</h2>
            </div>

            <div className="tab-nav">
              <TabButton
                label="Preferences"
                isActive={activeLeftTab === "preferences"}
                onClick={() => setActiveLeftTab("preferences")}
              />
              <TabButton
                label="Interests"
                isActive={activeLeftTab === "interests"}
                onClick={() => setActiveLeftTab("interests")}
              />
              <TabButton
                label="Map View"
                isActive={activeLeftTab === "mapview"}
                onClick={() => setActiveLeftTab("mapview")}
              />
            </div>

            {activeLeftTab === "preferences" && (
              <>
                <div className="filter-section">
                  <h3>Trip Preferences</h3>
                  <select className="dropdown">
                    <option>Travel Days: 3 Days</option>
                    <option>Travel Days: 5 Days</option>
                    <option>Travel Days: 7 Days</option>
                  </select>
                  <div style={{ marginTop: 10 }}>
                    <label style={{ fontSize: 14, color: "#6b7280" }}>
                      Budget Level
                    </label>
                    <div className="budget-input">
                      <span style={{ fontSize: 12 }}>$</span>
                      <input type="text" defaultValue="200-500" />
                      <span style={{ fontSize: 12 }}>/day</span>
                    </div>
                    <div className="budget-display">
                      Medium Budget
                      <br />
                      $500-1000/day
                    </div>
                  </div>
                </div>

                <div className="filter-section">
                  <h3>Luxury Experience</h3>
                  <div className="budget-input">
                    <span style={{ fontSize: 12 }}>$</span>
                    <input type="text" defaultValue="1000+" />
                    <span style={{ fontSize: 12 }}>/day</span>
                  </div>
                </div>

                <div className="filter-section">
                  <h3>Number of Travelers</h3>
                  <select className="dropdown">
                    <option>2 People</option>
                    <option>3 People</option>
                    <option>4+ People</option>
                  </select>
                </div>
              </>
            )}

            {activeLeftTab === "mapview" && (
              <div className="filter-section">
                <h3>Map Layers</h3>
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 10 }}
                >
                  <label className="filter-item">
                    <input type="checkbox" defaultChecked />
                    <span
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <span
                        style={{
                          width: 12,
                          height: 12,
                          borderRadius: "50%",
                          background: "#3b82f6",
                        }}
                      ></span>
                      Attractions
                    </span>
                  </label>
                  <label className="filter-item">
                    <input type="checkbox" defaultChecked />
                    <span
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <span
                        style={{
                          width: 12,
                          height: 12,
                          borderRadius: "50%",
                          background: "#ef4444",
                        }}
                      ></span>
                      Restaurants
                    </span>
                  </label>
                  <label className="filter-item">
                    <input type="checkbox" />
                    <span
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <span
                        style={{
                          width: 12,
                          height: 12,
                          borderRadius: "50%",
                          background: "#a855f7",
                        }}
                      ></span>
                      Shopping
                    </span>
                  </label>
                  <label className="filter-item">
                    <input type="checkbox" defaultChecked />
                    <span
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <span
                        style={{
                          width: 12,
                          height: 12,
                          borderRadius: "50%",
                          background: "#10b981",
                        }}
                      ></span>
                      Transportation
                    </span>
                  </label>
                </div>
              </div>
            )}

            <div className="filter-section">
              <h3>Quick Filters</h3>
              <div
                style={{ display: "flex", flexDirection: "column", gap: 10 }}
              >
                <label className="filter-item">
                  <input type="checkbox" defaultChecked />
                  <span>High-rated Spots (4.5+)</span>
                </label>
                <label className="filter-item">
                  <input type="checkbox" />
                  <span>Nearby Attractions (5km)</span>
                </label>
                <label className="filter-item">
                  <input type="checkbox" />
                  <span>Currently Open</span>
                </label>
                <label className="filter-item">
                  <input type="checkbox" />
                  <span>Budget Friendly</span>
                </label>
              </div>
            </div>

            <div className="filter-section">
              <h3>Current Settings</h3>
              <div className="info-box">
                <div className="info-row">
                  <span>Destination:</span>
                  <span style={{ fontWeight: 600, color: "#1f2937" }}>
                    {cityName}
                  </span>
                </div>
                <div className="info-row">
                  <span>Days:</span>
                  <span style={{ fontWeight: 600, color: "#1f2937" }}>
                    3 Days
                  </span>
                </div>
                <div className="info-row">
                  <span>Travelers:</span>
                  <span style={{ fontWeight: 600, color: "#1f2937" }}>
                    2 People
                  </span>
                </div>
                <div className="info-row">
                  <span>Interests:</span>
                  <span style={{ fontWeight: 600, color: "#1f2937" }}>
                    2 Selected
                  </span>
                </div>
              </div>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <div className="main-content">
          {/* Map Container */}
          <div className="map-container">
            <LoadScript
              googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
            >
              <MapComponent
                locations={mapLocations}
                selectedLocation={selectedItem}
                onMarkerClick={(location) => {
                  console.log("Clicked location:", location);
                }}
                center={{ lat: 45.5017, lng: -73.5673 }}
              />
            </LoadScript>

            <div className="map-controls">
              <button className="map-btn">+</button>
              <button className="map-btn">-</button>
              <button className="map-btn">⊙</button>
            </div>

            <div className="route-preview">
              <div style={{ display: "flex", gap: 30 }}>
                <div style={{ textAlign: "center" }}>
                  <div
                    style={{ fontSize: 18, fontWeight: 600, color: "#1f2937" }}
                  >
                    8
                  </div>
                  <div style={{ fontSize: 12, color: "#6b7280" }}>Places</div>
                </div>
                <div style={{ textAlign: "center" }}>
                  <div
                    style={{ fontSize: 18, fontWeight: 600, color: "#1f2937" }}
                  >
                    100%
                  </div>
                  <div style={{ fontSize: 12, color: "#6b7280" }}>Zoom</div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <button
                  style={{
                    padding: "8px 16px",
                    borderRadius: 6,
                    border: "1px solid #e5e7eb",
                    background: "#fff",
                    fontSize: 14,
                    cursor: "pointer",
                  }}
                >
                  Navigate
                </button>
                <button
                  style={{
                    padding: "8px 16px",
                    borderRadius: 6,
                    border: "none",
                    background: "#3b82f6",
                    color: "#fff",
                    fontSize: 14,
                    cursor: "pointer",
                  }}
                >
                  Explore Nearby
                </button>
              </div>
            </div>
          </div>

          {/* AI Chat */}
          <div className="ai-chat">
            <div className="chat-header">
              <h3 style={{ fontSize: 16, fontWeight: 600, color: "#1f2937" }}>
                AI Trip Planner
              </h3>
              <p style={{ fontSize: 12, color: "#6b7280", marginTop: 5 }}>
                Planning your {cityName} adventure
              </p>
            </div>

            <div className="chat-messages">
              <div className="message-bubble">
                <strong style={{ color: "#1f2937" }}>AI Assistant</strong>
                <br />
                Hi! I'm your AI travel assistant. I can help you create the
                perfect itinerary for {cityName}. What kind of experience are
                you looking for?
              </div>
              <div className="quick-actions">
                <button className="quick-btn">
                  Cultural & Historical Sites
                </button>
                <button className="quick-btn">Food & Local Cuisine</button>
                <button className="quick-btn">Art & Modern Culture</button>
                <button className="quick-btn">Nature & Parks</button>
              </div>
            </div>

            <div className="chat-input-area">
              <div className="chat-input-wrap">
                <input
                  className="chat-input"
                  type="text"
                  placeholder={`Ask me anything about your trip to ${cityName}...`}
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleSendChat()}
                />
                <button className="send-btn" onClick={handleSendChat}>
                  <i className="bi bi-send" />
                </button>
              </div>
              <div
                style={{
                  display: "flex",
                  gap: 10,
                  marginTop: 10,
                  flexWrap: "wrap",
                }}
              >
                <button
                  style={{
                    padding: "6px 12px",
                    background: "#f3f4f6",
                    border: "1px solid #e5e7eb",
                    borderRadius: 16,
                    fontSize: 12,
                    cursor: "pointer",
                  }}
                >
                  Show me top attractions
                </button>
                <button
                  style={{
                    padding: "6px 12px",
                    background: "#f3f4f6",
                    border: "1px solid #e5e7eb",
                    borderRadius: 16,
                    fontSize: 12,
                    cursor: "pointer",
                  }}
                >
                  Best local restaurants
                </button>
                <button
                  style={{
                    padding: "6px 12px",
                    background: "#f3f4f6",
                    border: "1px solid #e5e7eb",
                    borderRadius: 16,
                    fontSize: 12,
                    cursor: "pointer",
                  }}
                >
                  3-day itinerary
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Right Sidebar - Trip Overview */}
        <aside
          className={`sidebar right-sidebar ${
            rightCollapsed ? "collapsed" : ""
          }`}
        >
          <ToggleButton onClick={() => setRightCollapsed(!rightCollapsed)} />

          <div className="sidebar-content">
            <div className="sidebar-header">
              <h2>Trip Overview</h2>
            </div>

            <div className="tab-nav">
              <TabButton
                label="Itinerary"
                isActive={activeRightTab === "itinerary"}
                onClick={() => setActiveRightTab("itinerary")}
              />
              <TabButton
                label="Overview"
                isActive={activeRightTab === "overview"}
                onClick={() => setActiveRightTab("overview")}
              />
              <TabButton
                label="Smart Picks"
                isActive={activeRightTab === "smartpicks"}
                onClick={() => setActiveRightTab("smartpicks")}
              />
            </div>

            <div style={{ padding: 20 }}>
              {activeRightTab === "itinerary" && (
                <>
                  <div className="progress-card">
                    <h3
                      style={{
                        fontSize: 16,
                        fontWeight: 600,
                        marginBottom: 8,
                        color: "#1f2937",
                      }}
                    >
                      {cityName} Adventure
                    </h3>
                    <p
                      style={{
                        fontSize: 12,
                        color: "#6b7280",
                        marginBottom: 15,
                      }}
                    >
                      Completion Progress 65%
                    </p>
                    <div className="progress-bar">
                      <div className="progress-fill"></div>
                    </div>
                    <div className="progress-stats">
                      <div style={{ textAlign: "center" }}>
                        <div
                          style={{
                            fontSize: 24,
                            fontWeight: 700,
                            color: "#3b82f6",
                          }}
                        >
                          8
                        </div>
                        <div
                          style={{
                            fontSize: 12,
                            color: "#6b7280",
                            marginTop: 5,
                          }}
                        >
                          Completed
                        </div>
                      </div>
                      <div style={{ textAlign: "center" }}>
                        <div
                          style={{
                            fontSize: 24,
                            fontWeight: 700,
                            color: "#a855f7",
                          }}
                        >
                          12
                        </div>
                        <div
                          style={{
                            fontSize: 12,
                            color: "#6b7280",
                            marginTop: 5,
                          }}
                        >
                          To Do
                        </div>
                      </div>
                    </div>
                  </div>

                  <div style={{ marginTop: 20 }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: 15,
                      }}
                    >
                      <h3
                        style={{
                          fontSize: 16,
                          fontWeight: 600,
                          color: "#1f2937",
                        }}
                      >
                        Detailed Itinerary
                      </h3>
                      <button
                        style={{
                          padding: "6px 12px",
                          background: "#fff",
                          border: "1px solid #e5e7eb",
                          borderRadius: 6,
                          fontSize: 14,
                          cursor: "pointer",
                        }}
                      >
                        + Add
                      </button>
                    </div>

                    {itineraryItems.map((item, index) => (
                      <ItineraryItem
                        key={item.id}
                        item={item}
                        index={index}
                        isSelected={selectedItem === index}
                        onClick={handleItemSelect}
                      />
                    ))}
                  </div>

                  <div
                    style={{
                      marginTop: 20,
                      padding: 20,
                      background: "#f9fafb",
                      borderRadius: 12,
                    }}
                  >
                    <h3
                      style={{
                        fontSize: 16,
                        fontWeight: 600,
                        color: "#1f2937",
                        marginBottom: 15,
                      }}
                    >
                      Trip Statistics
                    </h3>
                    <div className="info-box" style={{ background: "#fff" }}>
                      <div className="info-row">
                        <span>Attractions:</span>
                        <span style={{ fontWeight: 600, color: "#1f2937" }}>
                          6 Places
                        </span>
                      </div>
                      <div className="info-row">
                        <span>Meals:</span>
                        <span style={{ fontWeight: 600, color: "#1f2937" }}>
                          3 Restaurants
                        </span>
                      </div>
                      <div className="info-row">
                        <span>Transport:</span>
                        <span style={{ fontWeight: 600, color: "#1f2937" }}>
                          Subway + Walk
                        </span>
                      </div>
                      <div className="info-row">
                        <span>Est. Budget:</span>
                        <span style={{ fontWeight: 600, color: "#1f2937" }}>
                          $1,280
                        </span>
                      </div>
                      <div className="info-row">
                        <span>Duration:</span>
                        <span style={{ fontWeight: 600, color: "#1f2937" }}>
                          3 Days
                        </span>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {activeRightTab === "smartpicks" && (
                <div
                  style={{
                    padding: 20,
                    background: "#f9fafb",
                    borderRadius: 12,
                  }}
                >
                  <h3
                    style={{
                      fontSize: 16,
                      fontWeight: 600,
                      marginBottom: 8,
                      color: "#1f2937",
                    }}
                  >
                    AI Smart Recommendations
                  </h3>
                  <p
                    style={{ fontSize: 12, color: "#6b7280", marginBottom: 15 }}
                  >
                    Based on your interests and itinerary
                  </p>

                  {recommendations.map((rec) => (
                    <div key={rec.id} className="rec-item">
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: "#1f2937",
                            marginBottom: 5,
                          }}
                        >
                          {rec.title}
                        </div>
                        <div
                          style={{
                            fontSize: 12,
                            color: "#6b7280",
                            marginBottom: 8,
                          }}
                        >
                          {rec.desc}
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 15,
                          }}
                        >
                          <span style={{ color: "#f59e0b", fontSize: 12 }}>
                            <i
                              className="bi bi-star-fill"
                              style={{ fontSize: 10 }}
                            />{" "}
                            {rec.rating}
                          </span>
                          <span style={{ color: "#6b7280", fontSize: 12 }}>
                            {rec.time}
                          </span>
                        </div>
                      </div>
                      <button
                        style={{
                          padding: "6px 12px",
                          background: "#fff",
                          border: "1px solid #e5e7eb",
                          borderRadius: 6,
                          fontSize: 14,
                          cursor: "pointer",
                        }}
                      >
                        + Add
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {activeRightTab === "overview" && (
                <div>
                  {/* 行程统计卡片 */}
                  <div
                    style={{
                      background: "#f9fafb",
                      padding: 20,
                      borderRadius: 12,
                      marginBottom: 20,
                    }}
                  >
                    <h3
                      style={{
                        fontSize: 16,
                        fontWeight: 600,
                        color: "#1f2937",
                        marginBottom: 15,
                      }}
                    >
                      Trip Statistics
                    </h3>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 12,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                        }}
                      >
                        <i
                          className="bi bi-geo-alt"
                          style={{ fontSize: 20, color: "#3b82f6" }}
                        />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 12, color: "#6b7280" }}>
                            Locations
                          </div>
                          <div
                            style={{
                              fontSize: 16,
                              fontWeight: 600,
                              color: "#1f2937",
                            }}
                          >
                            6 Places
                          </div>
                        </div>
                      </div>

                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                        }}
                      >
                        <i
                          className="bi bi-clock"
                          style={{ fontSize: 20, color: "#3b82f6" }}
                        />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 12, color: "#6b7280" }}>
                            Duration
                          </div>
                          <div
                            style={{
                              fontSize: 16,
                              fontWeight: 600,
                              color: "#1f2937",
                            }}
                          >
                            3 Days
                          </div>
                        </div>
                      </div>

                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                        }}
                      >
                        <i
                          className="bi bi-currency-dollar"
                          style={{ fontSize: 20, color: "#f59e0b" }}
                        />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 12, color: "#6b7280" }}>
                            Estimated Budget
                          </div>
                          <div
                            style={{
                              fontSize: 16,
                              fontWeight: 600,
                              color: "#1f2937",
                            }}
                          >
                            $1,280
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}


            </div>
          </div>
        </aside>
      </div>
    </>
  );
}
